import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { docsComercialesApi, exportApi, zonasApi } from '../services/api';
import { FileText, Upload, TrendingUp, RefreshCw, AlertTriangle, Clock, Truck, Receipt as ReceiptIcon, ArrowRight, BarChart3, ListChecks, Download, Loader2, Search, ChevronLeft, ChevronRight, GitBranch, X, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';
import DashboardVentasMetricas from '../components/ventas/DashboardVentasMetricas';

const TIPO_LABEL = {
  oferta: 'Oferta', pedido: 'Pedido', pedido_deposito: 'Ped. Depósito',
  albaran: 'Albarán', albaran_deposito: 'Alb. Depósito',
  factura: 'Factura', factura_liquidacion: 'Fact. Liquid.', factura_rectificativa: 'Fact. Rect.',
  reposicion: 'Reposición',
};
const TIPO_COLOR = {
  oferta: 'bg-sky-100 text-sky-700',
  pedido: 'bg-emerald-100 text-emerald-700',
  pedido_deposito: 'bg-teal-100 text-teal-700',
  albaran: 'bg-violet-100 text-violet-700',
  albaran_deposito: 'bg-purple-100 text-purple-700',
  factura: 'bg-amber-100 text-amber-700',
  factura_liquidacion: 'bg-orange-100 text-orange-700',
  factura_rectificativa: 'bg-red-100 text-red-700',
  reposicion: 'bg-slate-100 text-slate-600',
};
const EMPRESA_COLOR = { platecsa: 'bg-blue-100 text-blue-700', aguazor: 'bg-emerald-100 text-emerald-700' };
const fmtImporte = (n) => n != null ? `${Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fetchJson = (path) => fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then((r) => r.json());

export default function DocumentosComerciales() {
  const navigate = useNavigate();
  const [vista, setVista] = useState('dashboard'); // dashboard | pedidos | docs | operativa | importar
  const [opKpis, setOpKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [encallados, setEncallados] = useState([]);
  const [loadingEncallados, setLoadingEncallados] = useState(false);
  const [detalleTipo, setDetalleTipo] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await fetchJson('/api/documentos-comerciales/metricas/operacional');
      setOpKpis(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cargarEncallados = useCallback(async () => {
    setLoadingEncallados(true);
    try {
      const hoy = new Date();
      const corte = new Date(hoy - 200 * 86400000).toISOString().slice(0, 10);
      const res = await docsComercialesApi.estadoPedidos({ estado: 'todos', fecha_hasta: corte, limit: 200 });
      setEncallados(res.items || []);
    } catch { /* silencioso */ }
    finally { setLoadingEncallados(false); }
  }, []);

  useEffect(() => {
    if (vista === 'operativa') { cargar(); cargarEncallados(); }
  }, [vista, cargarEncallados]);

  const TabBtn = ({ k, label, icon: Icon, testid }) => (
    <button
      onClick={() => setVista(k)}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
        vista === k
          ? 'bg-brand-500 border-brand-500 text-white shadow-sm'
          : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
      }`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="documentos-comerciales">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-brand-500" />
            Ventas y Métricas
          </h1>
          <p className="text-ink-500 mt-1">Dashboard analítico · KPIs operacionales · Importación de datos ERP.</p>
        </div>
        <Button
          variant="outline"
          onClick={async () => { try { await exportApi.descargar('documentos-comerciales'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }}
          data-testid="btn-export-docs-comerciales"
        >
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap border-b border-ink-200 pb-3">
        <TabBtn k="dashboard" label="Dashboard analítico" icon={BarChart3} testid="vm-tab-dashboard" />
        <TabBtn k="pedidos" label="Estado Pedidos" icon={PackageCheck} testid="vm-tab-pedidos" />
        <TabBtn k="docs" label="Documentos" icon={FileText} testid="vm-tab-docs" />
        <TabBtn k="operativa" label="Operativa (pendientes)" icon={ListChecks} testid="vm-tab-operativa" />
        <TabBtn k="importar" label="Importar ERP" icon={Upload} testid="vm-tab-importar" />
      </div>

      {vista === 'dashboard' && <DashboardVentasMetricas />}
      {vista === 'pedidos' && <VistaPedidosEstado />}
      {vista === 'docs' && <VistaDocumentos />}

      {vista === 'operativa' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-recargar-docs">
              <RefreshCw className="w-4 h-4 mr-2" /> Recargar
            </Button>
          </div>
          {loading && <div className="text-ink-500">Calculando KPIs operacionales…</div>}
          {!loading && opKpis && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="op-kpis">
                <KpiCard
                  titulo="Ofertas pendientes"
                  icono={<FileText className="w-5 h-5" />}
                  valor={opKpis.ofertas_pendientes.total}
                  subvalor={`${(opKpis.ofertas_pendientes.importe || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`}
                  descripcion="Ofertas que no han generado pedido — comercial debe reclamar"
                  color="sky"
                  onClick={() => setDetalleTipo('ofertas_pendientes')}
                  testid="kpi-ofertas-pendientes"
                />
                <KpiCard
                  titulo="Pendientes de entrega"
                  icono={<Truck className="w-5 h-5" />}
                  valor={opKpis.pedidos_pendientes_entrega_total.total + opKpis.pedidos_servidos_parcialmente.total}
                  subvalor={`${(opKpis.importe_pendiente_entrega || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`}
                  descripcion={`${opKpis.pedidos_pendientes_entrega_total.total} totales · ${opKpis.pedidos_servidos_parcialmente.total} parciales`}
                  color="emerald"
                  onClick={() => setDetalleTipo('pendientes_entrega')}
                  testid="kpi-pendientes-entrega"
                />
                <KpiCard
                  titulo="Rompe-stock (>30d)"
                  icono={<AlertTriangle className="w-5 h-5" />}
                  valor={opKpis.pedidos_rompe_stock.total}
                  subvalor="Alerta"
                  descripcion="Pedidos con líneas pendientes más de 30 días"
                  color="rose"
                  onClick={() => setDetalleTipo('rompe_stock')}
                  testid="kpi-rompe-stock"
                />
                <KpiCard
                  titulo="Pendientes facturar"
                  icono={<ReceiptIcon className="w-5 h-5" />}
                  valor={opKpis.albaranes_pendientes_facturar.total}
                  subvalor={`${(opKpis.albaranes_pendientes_facturar.importe || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`}
                  descripcion="Albaranes que aún no tienen factura"
                  color="amber"
                  onClick={() => setDetalleTipo('pendientes_facturar')}
                  testid="kpi-pendientes-facturar"
                />
              </div>
              {detalleTipo && <ListaPendientes opKpis={opKpis} tipo={detalleTipo} onClose={() => setDetalleTipo(null)} />}

              {/* Pedidos encallados */}
              <Card className="p-4 border-red-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-ink-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Pedidos encallados (&gt;200 días)
                    {!loadingEncallados && (
                      <Badge className="bg-red-100 text-red-700 ml-1">{encallados.length}</Badge>
                    )}
                  </h3>
                  {loadingEncallados && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {encallados.length === 0 && !loadingEncallados ? (
                  <p className="text-sm text-slate-400">Sin pedidos encallados. ✓</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b text-slate-500">
                        <tr>
                          <th className="pb-1.5 font-medium text-left">Pedido</th>
                          <th className="pb-1.5 font-medium text-left">Cliente</th>
                          <th className="pb-1.5 font-medium text-left">Fecha</th>
                          <th className="pb-1.5 font-medium text-right">Importe pdte.</th>
                          <th className="pb-1.5 font-medium text-right">Días</th>
                          <th className="pb-1.5 font-medium text-right">% Servido</th>
                          <th className="pb-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {encallados.map(doc => {
                          const dias = doc.dias_pendiente ?? 0;
                          const diasClass = dias > 200 ? 'text-red-600 font-bold' : 'text-amber-600 font-semibold';
                          return (
                            <tr key={doc.id} className="border-b last:border-0 hover:bg-red-50">
                              <td className="py-1.5 font-mono font-semibold text-slate-700">{doc.numero}</td>
                              <td className="py-1.5 text-slate-600 max-w-[180px] truncate">{doc.cliente_nombre_erp || '—'}</td>
                              <td className="py-1.5 text-slate-500">{doc.fecha || '—'}</td>
                              <td className="py-1.5 text-right font-mono text-amber-700">{fmtImporte(doc.importe_pendiente)}</td>
                              <td className={`py-1.5 text-right tabular-nums ${diasClass}`}>{dias}d</td>
                              <td className="py-1.5 text-right text-slate-500">{doc.porcentaje_servido?.toFixed(0)}%</td>
                              <td className="py-1.5 text-right">
                                <button
                                  className="text-red-500 hover:text-red-700 hover:underline text-xs px-1"
                                  onClick={async () => {
                                    if (!window.confirm(`¿Marcar ${doc.numero} como anulado?`)) return;
                                    try {
                                      await docsComercialesApi.anularDocumento(doc.id);
                                      setEncallados(prev => prev.filter(d => d.id !== doc.id));
                                      toast.success(`${doc.numero} marcado como anulado`);
                                    } catch (e) { toast.error('Error: ' + e.message); }
                                  }}
                                >
                                  Anular
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card className="p-4">
                <h3 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-brand-500" /> Flujo comercial (pendientes)
                </h3>
                <div className="flex items-center justify-between gap-1 overflow-x-auto">
                  <EmbudoStep color="sky" label="Ofertas" valor={opKpis.ofertas_pendientes.total} sub="pendientes" />
                  <ArrowRight className="text-ink-300 shrink-0" />
                  <EmbudoStep color="emerald" label="Pedidos" valor={opKpis.pedidos_pendientes_entrega_total.total + opKpis.pedidos_servidos_parcialmente.total} sub="pdtes entrega" />
                  <ArrowRight className="text-ink-300 shrink-0" />
                  <EmbudoStep color="violet" label="Albaranes" valor={opKpis.albaranes_pendientes_facturar.total} sub="pdtes facturar" />
                  <ArrowRight className="text-ink-300 shrink-0" />
                  <EmbudoStep color="amber" label="Facturas" valor="✓" sub="cerrado" />
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {vista === 'importar' && (
        <Card className="p-10 border-ink-200 bg-white text-center" data-testid="vm-importar-panel">
          <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
            <Upload className="w-12 h-12 text-ink-300" />
            <div>
              <h3 className="text-lg font-semibold text-ink-900">Importar datos del ERP</h3>
              <p className="text-sm text-ink-500 mt-1">
                Importa facturas en PDF y pedidos en CSV desde el panel dedicado.
                Admite importación masiva con seguimiento de progreso en tiempo real.
              </p>
            </div>
            <Button onClick={() => navigate('/erp/importar')} data-testid="btn-ir-importacion-erp">
              <ArrowRight className="w-4 h-4 mr-2" /> Ir al panel de importación
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// Vista Estado Pedidos — tabla general paginada
// ============================================================
const ESTADO_PEDIDO_COLOR = {
  pendiente: 'bg-red-100 text-red-700',
  parcial:   'bg-amber-100 text-amber-700',
  servido:   'bg-emerald-100 text-emerald-700',
};
const ESTADO_PEDIDO_LABEL = { pendiente: 'Pendiente', parcial: 'Parcial', servido: 'Servido' };

function BarraPorcentaje({ pct }) {
  const color = pct >= 99 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs text-slate-600 tabular-nums">{pct.toFixed(0)}%</span>
    </div>
  );
}

function VistaPedidosEstado() {
  const PAGE_SIZE = 50;
  const emptyF = { empresa: '', estado: 'todos', fecha_desde: '', fecha_hasta: '' };
  const [filtros, setFiltros] = useState(emptyF);
  const [aplicados, setAplicados] = useState(emptyF);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);

  const buscar = useCallback(async (f, s = 0) => {
    setLoading(true);
    try {
      const params = { skip: s, limit: PAGE_SIZE };
      if (f.empresa) params.empresa = f.empresa;
      if (f.estado && f.estado !== 'todos') params.estado = f.estado;
      if (f.fecha_desde) params.fecha_desde = f.fecha_desde;
      if (f.fecha_hasta) params.fecha_hasta = f.fecha_hasta;
      const res = await docsComercialesApi.estadoPedidos(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
      setSkip(s);
    } catch { toast.error('Error cargando pedidos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { buscar(emptyF, 0); }, []); // eslint-disable-line

  const aplicar = () => { setAplicados({ ...filtros }); buscar(filtros, 0); };
  const limpiar = () => { setFiltros(emptyF); setAplicados(emptyF); buscar(emptyF, 0); };
  const paginar = (dir) => { const s = Math.max(0, skip + dir * PAGE_SIZE); buscar(aplicados, s); };
  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));

  const totalPags = Math.ceil(total / PAGE_SIZE);
  const pagActual = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Empresa</label>
            <select value={filtros.empresa} onChange={e => setF('empresa', e.target.value)} className="border rounded px-2 py-1.5 text-sm">
              <option value="">Todas</option>
              <option value="platecsa">Platecsa</option>
              <option value="aguazor">Aguazor</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Estado</label>
            <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} className="border rounded px-2 py-1.5 text-sm">
              <option value="todos">Todos (sin servidos)</option>
              <option value="pendiente">Solo pendientes</option>
              <option value="parcial">Solo parciales</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Desde</label>
            <input type="date" value={filtros.fecha_desde} onChange={e => setF('fecha_desde', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Hasta</label>
            <input type="date" value={filtros.fecha_hasta} onChange={e => setF('fecha_hasta', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          </div>
          <Button size="sm" onClick={aplicar} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
            Buscar
          </Button>
          <Button size="sm" variant="outline" onClick={limpiar}><X className="w-3.5 h-3.5" /></Button>
        </div>
      </Card>

      {/* Contador y paginación */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{loading ? 'Calculando…' : `${total.toLocaleString('es-ES')} pedidos abiertos`}</span>
        {total > PAGE_SIZE && (
          <div className="flex items-center gap-2">
            <button onClick={() => paginar(-1)} disabled={skip === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span>Pág {pagActual} / {totalPags}</span>
            <button onClick={() => paginar(1)} disabled={pagActual >= totalPags} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {/* Tabla */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Calculando estados…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-400">Sin pedidos abiertos con los filtros actuales.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2.5 font-medium">Pedido</th>
                  <th className="px-3 py-2.5 font-medium">Empresa</th>
                  <th className="px-3 py-2.5 font-medium">Cliente</th>
                  <th className="px-3 py-2.5 font-medium">Fecha</th>
                  <th className="px-3 py-2.5 font-medium text-right">Importe</th>
                  <th className="px-3 py-2.5 font-medium text-right">Pendiente</th>
                  <th className="px-3 py-2.5 font-medium">% Servido</th>
                  <th className="px-3 py-2.5 font-medium">Estado</th>
                  <th className="px-3 py-2.5 font-medium text-right">Días</th>
                </tr>
              </thead>
              <tbody>
                {items.map(doc => {
                  const dias = doc.dias_pendiente ?? 0;
                  const diasColor = dias > 200 ? 'text-red-600 font-bold' : dias > 90 ? 'text-amber-600 font-semibold' : 'text-slate-600';
                  return (
                    <tr key={doc.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-700">{doc.numero}</td>
                      <td className="px-3 py-2">
                        {doc.empresa
                          ? <Badge className={`text-xs ${EMPRESA_COLOR[doc.empresa] || 'bg-slate-100'}`}>{doc.empresa}</Badge>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate text-xs" title={doc.cliente_nombre_erp}>
                        {doc.cliente_nombre_erp || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{doc.fecha || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-slate-600">{fmtImporte(doc.importe_total)}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-amber-700">{fmtImporte(doc.importe_pendiente)}</td>
                      <td className="px-3 py-2"><BarraPorcentaje pct={doc.porcentaje_servido} /></td>
                      <td className="px-3 py-2">
                        <Badge className={`text-xs ${ESTADO_PEDIDO_COLOR[doc.estado_calculado] || 'bg-slate-100'}`}>
                          {ESTADO_PEDIDO_LABEL[doc.estado_calculado] || doc.estado_calculado}
                        </Badge>
                      </td>
                      <td className={`px-3 py-2 text-right text-xs tabular-nums ${diasColor}`}>{dias}d</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// Vista Documentos — tabla navegable con filtros
// ============================================================
const TIPOS_OPCIONES = [
  { value: '', label: 'Todos los tipos' },
  { value: 'oferta', label: 'Ofertas' },
  { value: 'pedido,pedido_deposito', label: 'Pedidos' },
  { value: 'albaran,albaran_deposito', label: 'Albaranes' },
  { value: 'factura,factura_liquidacion,factura_rectificativa', label: 'Facturas' },
  { value: 'reposicion', label: 'Reposiciones' },
];

function PanelTrazabilidad({ docId, onClose }) {
  const [cadena, setCadena] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    docsComercialesApi.trazabilidad(docId).then(r => { setCadena(r); setLoading(false); }).catch(() => setLoading(false));
  }, [docId]);
  const ICONOS = { oferta: '📋', pedido: '🛒', pedido_deposito: '🏪', albaran: '🚚', albaran_deposito: '🏭', factura: '🧾', factura_liquidacion: '💰', factura_rectificativa: '↩️', reposicion: '🔄' };
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-slate-800">Trazabilidad del documento</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-auto flex-1 px-5 py-4">
          {loading && <p className="text-slate-400">Cargando…</p>}
          {!loading && !cadena && <p className="text-slate-400">No se pudo cargar</p>}
          {cadena && (
            <div className="space-y-2">
              {cadena.cadena.map((d, i) => (
                <div key={d.id} className="flex items-start gap-3">
                  {i > 0 && <div className="w-px h-4 bg-slate-200 ml-3 -mt-2" />}
                  <div className={`flex-1 rounded-lg border p-3 ${TIPO_COLOR[d.tipo] || 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <span>{ICONOS[d.tipo] || '📄'}</span>
                      <span>{d.numero}</span>
                      <Badge className={`text-xs ${TIPO_COLOR[d.tipo]}`}>{TIPO_LABEL[d.tipo] || d.tipo}</Badge>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{d.cliente_nombre_erp} · {d.fecha} · {fmtImporte(d.importe_total)}</div>
                    {d.empresa && <div className="text-xs mt-0.5"><Badge className={`text-xs ${EMPRESA_COLOR[d.empresa] || 'bg-slate-100'}`}>{d.empresa}</Badge></div>}
                  </div>
                </div>
              ))}
              {cadena.cadena.length === 0 && <p className="text-slate-400 text-sm">Sin cadena de trazabilidad</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VistaDocumentos() {
  const PAGE_SIZE = 50;
  const emptyFiltros = { tipo: '', empresa: '', zona_id: '', q: '', fecha_desde: '', fecha_hasta: '' };
  const [filtros, setFiltros] = useState(emptyFiltros);
  const [aplicados, setAplicados] = useState(emptyFiltros);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [zonas, setZonas] = useState([]);
  const [trazDoc, setTrazDoc] = useState(null);

  useEffect(() => { zonasApi.list().then(r => setZonas(r.items || r || [])).catch(() => {}); }, []);

  const buscar = useCallback(async (filtrosActivos, pagSkip = 0) => {
    setLoading(true);
    try {
      const params = { skip: pagSkip, limit: PAGE_SIZE };
      if (filtrosActivos.tipo) params.tipo = filtrosActivos.tipo;
      if (filtrosActivos.empresa) params.empresa = filtrosActivos.empresa;
      if (filtrosActivos.zona_id) params.zona_id = filtrosActivos.zona_id;
      if (filtrosActivos.q) params.q = filtrosActivos.q;
      if (filtrosActivos.fecha_desde) params.fecha_desde = filtrosActivos.fecha_desde;
      if (filtrosActivos.fecha_hasta) params.fecha_hasta = filtrosActivos.fecha_hasta;
      const res = await docsComercialesApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
      setSkip(pagSkip);
    } catch { toast.error('Error cargando documentos'); }
    finally { setLoading(false); }
  }, []);

  const aplicarFiltros = () => { setAplicados({ ...filtros }); buscar(filtros, 0); };
  const limpiar = () => { setFiltros(emptyFiltros); setAplicados(emptyFiltros); buscar(emptyFiltros, 0); };
  const paginar = (dir) => { const s = Math.max(0, skip + dir * PAGE_SIZE); buscar(aplicados, s); };

  useEffect(() => { buscar(emptyFiltros, 0); }, []); // eslint-disable-line

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const totalPags = Math.ceil(total / PAGE_SIZE);
  const pagActual = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label>
            <select value={filtros.tipo} onChange={e => setF('tipo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
              {TIPOS_OPCIONES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Empresa</label>
            <select value={filtros.empresa} onChange={e => setF('empresa', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">Todas</option>
              <option value="platecsa">Platecsa</option>
              <option value="aguazor">Aguazor</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Zona</label>
            <select value={filtros.zona_id} onChange={e => setF('zona_id', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">Todas las zonas</option>
              {zonas.map(z => <option key={z.id || z._id} value={z.id || z._id}>{z.nombre}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1">Cliente</label>
            <div className="relative">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
              <input value={filtros.q} onChange={e => setF('q', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && aplicarFiltros()}
                placeholder="Buscar por nombre…" className="w-full border rounded pl-7 pr-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" onClick={aplicarFiltros} disabled={loading} className="flex-1">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              <span className="ml-1">Buscar</span>
            </Button>
            <Button size="sm" variant="outline" onClick={limpiar} title="Limpiar filtros"><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3 max-w-xs">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Desde</label>
            <input type="date" value={filtros.fecha_desde} onChange={e => setF('fecha_desde', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Hasta</label>
            <input type="date" value={filtros.fecha_hasta} onChange={e => setF('fecha_hasta', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
      </Card>

      {/* Resultados */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{loading ? 'Buscando…' : `${total.toLocaleString('es-ES')} documentos`}</span>
        {total > PAGE_SIZE && (
          <div className="flex items-center gap-2">
            <button onClick={() => paginar(-1)} disabled={skip === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span>Pág {pagActual} / {totalPags}</span>
            <button onClick={() => paginar(1)} disabled={pagActual >= totalPags} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-400">Sin resultados. Ajusta los filtros.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2.5 font-medium">Nº doc</th>
                  <th className="px-3 py-2.5 font-medium">Tipo</th>
                  <th className="px-3 py-2.5 font-medium">Empresa</th>
                  <th className="px-3 py-2.5 font-medium">Cliente</th>
                  <th className="px-3 py-2.5 font-medium">Fecha</th>
                  <th className="px-3 py-2.5 font-medium text-right">Importe</th>
                  <th className="px-3 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(doc => (
                  <tr key={doc.id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-700">{doc.numero}</td>
                    <td className="px-3 py-2">
                      <Badge className={`text-xs ${TIPO_COLOR[doc.tipo] || 'bg-slate-100 text-slate-600'}`}>
                        {TIPO_LABEL[doc.tipo] || doc.tipo}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {doc.empresa
                        ? <Badge className={`text-xs ${EMPRESA_COLOR[doc.empresa] || 'bg-slate-100'}`}>{doc.empresa}</Badge>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate" title={doc.cliente_nombre_erp}>
                      {doc.cliente_nombre_erp || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{doc.fecha || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-700">{fmtImporte(doc.importe_total)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setTrazDoc(doc.id)}
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-600 px-2 py-1 rounded hover:bg-slate-100">
                        <GitBranch className="w-3 h-3" /> Trazabilidad
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {trazDoc && <PanelTrazabilidad docId={trazDoc} onClose={() => setTrazDoc(null)} />}
    </div>
  );
}

function KpiCard({ titulo, icono, valor, subvalor, descripcion, color, onClick, testid }) {
  const palette = {
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <Card
      className={`p-4 border ${palette[color]} cursor-pointer hover:shadow-md transition-shadow`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg ${palette[color]} flex items-center justify-center`}>{icono}</div>
      </div>
      <div className="text-3xl font-bold text-ink-900 leading-tight">{valor}</div>
      <div className="text-sm font-semibold text-ink-700 mt-0.5">{titulo}</div>
      <div className="text-xs text-ink-500 mt-0.5">{subvalor}</div>
      <p className="text-xs text-ink-500 mt-2 leading-tight">{descripcion}</p>
    </Card>
  );
}

function EmbudoStep({ color, label, valor, sub }) {
  const palette = {
    sky: 'bg-sky-100 text-sky-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    violet: 'bg-violet-100 text-violet-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return (
    <div className={`flex-1 min-w-[110px] text-center px-3 py-3 rounded-lg ${palette[color]}`}>
      <div className="text-2xl font-bold">{valor}</div>
      <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className="text-[10px] opacity-75">{sub}</div>
    </div>
  );
}

function ListaPendientes({ opKpis, tipo, onClose }) {
  const mapa = {
    ofertas_pendientes: { titulo: 'Ofertas pendientes (sin pedido)', items: opKpis.ofertas_pendientes.items, campos: ['numero', 'fecha', 'cliente_nombre_erp', 'importe_total'] },
    pendientes_entrega: {
      titulo: 'Pedidos pendientes de entrega',
      items: [
        ...opKpis.pedidos_pendientes_entrega_total.items.map((p) => ({ ...p, _tipo: 'Total' })),
        ...opKpis.pedidos_servidos_parcialmente.items.map((p) => ({ ...p, _tipo: 'Parcial' })),
      ],
      campos: ['numero', 'fecha', 'cliente_nombre_erp', 'importe_total', 'porcentaje_servido', 'dias'],
    },
    rompe_stock: {
      titulo: 'Pedidos con alerta de rompe-stock (>30 días pendientes)',
      items: opKpis.pedidos_rompe_stock.items,
      campos: ['numero', 'fecha', 'cliente_nombre_erp', 'importe_total', 'porcentaje_servido', 'dias'],
    },
    pendientes_facturar: {
      titulo: 'Albaranes pendientes de facturar',
      items: opKpis.albaranes_pendientes_facturar.items,
      campos: ['numero', 'fecha', 'cliente_nombre_erp', 'importe_total'],
    },
  };
  const d = mapa[tipo];
  if (!d) return null;
  return (
    <Card className="p-4" data-testid={`lista-${tipo}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-ink-900">{d.titulo} ({d.items.length})</h3>
        <Button size="sm" variant="outline" onClick={onClose}>Cerrar</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-ink-500 text-xs uppercase border-b border-ink-200">
            <tr>
              <th className="text-left py-2 px-2">Nº</th>
              <th className="text-left py-2 px-2">Fecha</th>
              <th className="text-left py-2 px-2">Cliente</th>
              <th className="text-right py-2 px-2">Importe</th>
              {d.campos.includes('porcentaje_servido') && <th className="text-right py-2 px-2">% servido</th>}
              {d.campos.includes('dias') && <th className="text-right py-2 px-2">Días</th>}
              {tipo === 'pendientes_entrega' && <th className="text-left py-2 px-2">Tipo</th>}
            </tr>
          </thead>
          <tbody>
            {d.items.slice(0, 50).map((r, i) => (
              <tr key={i} className={i % 2 ? 'bg-ink-50/40' : ''} data-testid={`lista-row-${i}`}>
                <td className="py-1.5 px-2 font-mono text-xs font-medium">{r.numero}</td>
                <td className="py-1.5 px-2 text-xs">{r.fecha}</td>
                <td className="py-1.5 px-2">
                  <div className="text-ink-900 text-sm">{r.cliente_nombre_erp}</div>
                  <div className="text-xs text-ink-400">{r.cliente_codigo_erp}</div>
                </td>
                <td className="py-1.5 px-2 text-right font-medium">{(r.importe_total || 0).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €</td>
                {d.campos.includes('porcentaje_servido') && (
                  <td className="py-1.5 px-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <div className="w-14 h-1.5 bg-ink-100 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${r.porcentaje_servido || 0}%` }} />
                      </div>
                      <span className="text-xs font-medium">{r.porcentaje_servido}%</span>
                    </div>
                  </td>
                )}
                {d.campos.includes('dias') && (
                  <td className="py-1.5 px-2 text-right">
                    <span className={`text-xs font-medium ${r.dias > 30 ? 'text-rose-600' : r.dias > 15 ? 'text-amber-600' : 'text-ink-500'}`}>
                      {r.dias}d
                    </span>
                  </td>
                )}
                {tipo === 'pendientes_entrega' && (
                  <td className="py-1.5 px-2">
                    <Badge className={r._tipo === 'Total' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}>
                      {r._tipo}
                    </Badge>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.items.length > 50 && <p className="text-xs text-ink-500 mt-2">Mostrando 50 de {d.items.length}. Exportación completa disponible en ficha de cliente.</p>}
    </Card>
  );
}
